import { useSelectedDealer, useSelectedLocation } from "components";
import ENV from "config/Env";
import { typedQueryClient, useTypedMutation } from "hooks";
import { ROLES } from "models";
import { ChecklistsDashboardQueryKeys } from "modules/ChecklistsDashboard/queryKeys";
import { getChecklistById } from "modules/ChecklistsDashboard/util";
import { ApiInstance } from "utils/Api";

import type { ChecklistID, DealerID, DealerLocationID } from "models";

type ExtendChecklistPayload = {
  parent_id: ChecklistID;
  name: string;
  dealer_id?: DealerID;
  dealer_location_id?: DealerLocationID;
};

export const useExtendChecklistMutation = () => {
  const { selectedDealer } = useSelectedDealer();
  const { selectedLocation } = useSelectedLocation();

  return useTypedMutation<ExtendChecklistPayload, ChecklistID>({
    mutationFn: async payload => (await ApiInstance.post("/checklists/extend", payload, ENV.dealerBaseURL)).data.id,
    onSuccess: ({ parent_id, name, dealer_id, dealer_location_id }, id) => {
      const parentChecklist = getChecklistById(parent_id, selectedDealer?.id, selectedLocation?.id);
      if (!parentChecklist) return;

      const extendedChecklist = {
        ...parentChecklist,
        id,
        name,
        dealer_id: dealer_id ?? selectedLocation?.dealer_id ?? null,
        role_id: ROLES.DealerAdmin,
        parent_checklist: parentChecklist
      };

      if (dealer_id) {
        const dealerChecklists = typedQueryClient.getQueryData(ChecklistsDashboardQueryKeys.dealerChecklists(dealer_id)) ?? [];
        typedQueryClient.setQueryData(ChecklistsDashboardQueryKeys.dealerChecklists(dealer_id), [extendedChecklist, ...dealerChecklists]);
      }

      if (!dealer_location_id) return;

      const locationChecklists = typedQueryClient.getQueryData(ChecklistsDashboardQueryKeys.locationChecklists(dealer_location_id)) ?? [];
      const lastDisplayOrder = locationChecklists.reduce((maxDisplayOrder, lc) => Math.max(maxDisplayOrder, lc.display_order ?? 0), 0);

      typedQueryClient.setQueryData(ChecklistsDashboardQueryKeys.locationChecklists(dealer_location_id), [
        ...locationChecklists,
        { ...extendedChecklist, display_order: lastDisplayOrder + 1 }
      ]);
    }
  });
};
